import { registerBaseURL } from '#constants/hosts';
import { client } from '#queries/utils';
import type { JSONApiDataEntity, ToOneRelationshipData } from '@repo/api-kit/types';

interface RedirectionTokenJsonApiResponse {
  data: JSONApiDataEntity<
    Record<string, unknown>,
    { partner_registration: ToOneRelationshipData }
  > & {
    relationships: { partner_registration: ToOneRelationshipData };
  };
}

interface PartnerRegistrationJsonApiResponse {
  data: JSONApiDataEntity<
    { organization_id?: string },
    { organization?: ToOneRelationshipData }
  > & {
    relationships?: { organization?: ToOneRelationshipData };
  };
}

/**
 * Resolves the organization id linked to a redirection token via two hops:
 *
 *   1. `GET /v1/redirection_tokens/:tokenId` — JSON:API; the only field
 *      consumed is `data.relationships.partner_registration.data.id`.
 *   2. `GET /v1/partner_registrations/:partnerRegistrationId` — JSON:API;
 *      the organization id sits at
 *      `data.relationships.organization.data.id` on the wire (with a
 *      fallback to `data.attributes.organization_id` for BE configs that
 *      put it in attributes).
 *
 * Neither endpoint has a proto definition today, so the JSON:API parsing
 * lives here in api-kit rather than in the kit/app routes that consume it.
 *
 * The shared `client()` has a fetch interceptor that pushes the second
 * response (with its sideloaded organization) into the Ember Data store,
 * so the downstream Ember `invoicing-organization` route's `findRecord`
 * resolves from the store without issuing another GET.
 */
export async function resolveRedirectionTokenOrganization(
  tokenId: string
): Promise<{ organizationId: string }> {
  const tokenResponse = await client<RedirectionTokenJsonApiResponse>(
    `${registerBaseURL}/v1/redirection_tokens/${tokenId}`
  );
  const partnerRegistrationId = tokenResponse.data.relationships.partner_registration.data.id;

  if (typeof partnerRegistrationId !== 'string' || partnerRegistrationId.length === 0) {
    throw new Error('Redirection token is not linked to a partner registration');
  }

  const partnerRegResponse = await client<PartnerRegistrationJsonApiResponse>(
    `${registerBaseURL}/v1/partner_registrations/${partnerRegistrationId}`
  );
  const organizationId =
    partnerRegResponse.data.relationships?.organization?.data.id ??
    partnerRegResponse.data.attributes.organization_id;

  if (typeof organizationId !== 'string' || organizationId.length === 0) {
    throw new Error(
      `Partner registration ${partnerRegistrationId} is not linked to an organization`
    );
  }

  return { organizationId };
}
